import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, User, Car, Clock, DollarSign, ChevronRight, Bot, Users, ArrowLeft } from 'lucide-react';
import useAuth from '../../hooks/useAuth';
import toast from 'react-hot-toast';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #202124;
  color: #ffffff;
  font-family: 'Clash Grotesk', sans-serif; 
  width: 90%;
  margin: 0 auto; 
`;

const Header = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1.5rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease; 
  flex: none;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-1px);
  }
`;

const HeaderTitle = styled.h1`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 8px;
`;

const HeaderStats = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
`;

const StatBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  background: ${props => props.color || 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.textColor || 'rgba(255, 255, 255, 0.8)'};
  font-weight: 500;
`;

const RefreshButton = styled.button`
  background: rgba(244, 63, 96, 0.1);
  border: 1px solid rgba(244, 63, 96, 0.3);
  color: #F43F60;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(244, 63, 96, 0.2);
    transform: translateY(-1px);
  }
`;

const ChatList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
`;

const ChatItem = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  ${props => props.hasDealerConnection && `
    background: rgba(59, 130, 246, 0.05);
    border-left: 4px solid #3B82F6;
  `}
  
  &:hover {
    background: ${props => props.hasDealerConnection ? 'rgba(59, 130, 246, 0.08)' : 'rgba(255, 255, 255, 0.08)'};
    transform: translateY(-2px);
  }
`;

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const SessionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const SessionIcon = styled.div`
  width: 40px;
  height: 40px;
  background: ${props => props.hasDealerConnection ? 'linear-gradient(45deg, #3B82F6, #1D4ED8)' : 'linear-gradient(45deg, #F43F60, rgb(247, 90, 118))'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 30px;
  position: relative;
    svg {
    height: 28px;
    width: 28px;
  }
`;

const ConnectionStatus = styled.div`
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.connected ? '#22C55E' : '#EF4444'};
  border: 2px solid #202124;
`;

const SessionDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const SessionTitle = styled.span`
  font-weight: 600;
  font-size: 1rem;
  color: #ffffff;
`;

const SessionSubtitle = styled.span`
  ffont-size: 16px
  color: rgba(255, 255, 255, 0.7);
`;

const SessionCreatedAt = styled.span`
   font-size:14px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 0.25rem;
`;

const ChatTime = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size:16px;
  color: rgba(255, 255, 255, 0.6);
`;

const DealerConnectionBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 500;
  background: rgba(59, 130, 246, 0.1);
  color: #3B82F6;
  border: 1px solid rgba(59, 130, 246, 0.3);
`;

const VehicleInfo = styled.div`
  background: rgba(244, 63, 96, 0.1);
  border: 1px solid rgba(244, 63, 96, 0.2);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const VehicleIcon = styled.div`
  width: 32px;
  height: 32px;
  background: linear-gradient(45deg, #F43F60, rgb(247, 90, 118));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
`;

const VehicleDetails = styled.div`
  flex: 1;
`;

const VehicleTitle = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  color: #F43F60;
  margin-bottom: 0.25rem;
`;

const VehicleSpecs = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const DealerInfo = styled.div`
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const DealerIcon = styled.div`
  width: 32px;
  height: 32px;
  background: linear-gradient(45deg, #3B82F6, #1D4ED8);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
`;

const DealerDetails = styled.div`
  flex: 1;
`;

const DealerTitle = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  color: #3B82F6;
  margin-bottom: 0.25rem;
`;

const DealerSubtitle = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
`;

const LatestMessage = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const MessageContent = styled.p`
  margin: 0;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.4;
`;

const MessageMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.5rem;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
`;

const ChatStats = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const OfferInfo = styled.div`
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.2);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #22C55E;
  font-weight: 500;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  padding: 2rem;
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(255, 255, 255, 0.6);
`;

const UserChatHistory = () => {
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const navigate = useNavigate();
  const { user } = useAuth();

  const fetchChatHistory = async (page = 1) => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/messages/user/chat-history?page=${page}&limit=20`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch chat history');
      }

      const data = await response.json();
      setChatHistory(data.data.chatHistory);
      setPagination(data.data.pagination);
    } catch (error) {
      console.error('Error fetching chat history:', error);
      toast.error('Failed to load chat history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChatHistory();
  }, []);

  const handleChatClick = (chat) => {
    console.log("chat: ", chat);
    navigate(`/user/chat/${chat.sessionId}`, {
      state: {
        sessionId: chat.sessionId,
        dealer: chat.dealer,
        vehicle: chat.vehicle,
        offer: chat.offer,
        sessionInfo: chat.sessionInfo,
        chatStats: chat.chatStats
      }
    });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getSessionIcon = (hasDealerConnection) => {
    return hasDealerConnection ? <Users size={16} /> : <Bot size={16} />;
  };

  const formatPrice = (price) => {
    if (!price) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatSessionCreatedAt = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInDays = (now - date) / (1000 * 60 * 60 * 24);

    if (diffInDays < 1) {
      return 'Today';
    } else if (diffInDays < 2) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return `${Math.floor(diffInDays)} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getChatPriority = (chat) => {
    // Priority order: dealer connection > recent activity
    if (chat.sessionInfo.hasDealerConnection) return 3;
    if (chat.latestMessage) return 2;
    return 1;
  };

  const sortChatsByPriority = (chats) => {
    return [...chats].sort((a, b) => {
      const priorityA = getChatPriority(a);
      const priorityB = getChatPriority(b);
      
      if (priorityA !== priorityB) {
        return priorityB - priorityA; // Higher priority first
      }
      
      // If same priority, sort by latest message time
      const timeA = a.latestMessage?.timestamp || a.chatStats?.firstMessageTime || 0;
      const timeB = b.latestMessage?.timestamp || b.chatStats?.firstMessageTime || 0;
      return new Date(timeB) - new Date(timeA);
    });
  };

  const getChatStats = () => {
    const stats = {
      total: chatHistory.length,
      withDealer: chatHistory.filter(chat => chat.sessionInfo.hasDealerConnection).length,
      withVehicle: chatHistory.filter(chat => chat.vehicle).length,
      withOffer: chatHistory.filter(chat => chat.offer).length,
      connected: chatHistory.filter(chat => chat.sessionInfo.customerConnected).length
    };
    return stats;
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <HeaderTitle>
            <MessageCircle size={24} />
            Chat History
          </HeaderTitle>
        </Header>
        <LoadingState>
          <div>Loading chats...</div>
        </LoadingState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/user/dashboard')}>
            <ArrowLeft size={20} />
          </BackButton>
          <div>
            <HeaderTitle>
              <MessageCircle size={24} />
              Chat History
            </HeaderTitle>
            <HeaderStats>
              <StatBadge color="rgba(59, 130, 246, 0.1)" textColor="#3B82F6">
                {getChatStats().withDealer} Dealer Chats
              </StatBadge>
              <StatBadge color="rgba(244, 63, 96, 0.1)" textColor="#F43F60">
                {getChatStats().withVehicle} Vehicle Inquiries
              </StatBadge>
              <StatBadge color="rgba(34, 197, 94, 0.1)" textColor="#22C55E">
                {getChatStats().withOffer} Offers
              </StatBadge>
              <StatBadge color="rgba(59, 130, 246, 0.1)" textColor="#3B82F6">
                {getChatStats().connected} Connected
              </StatBadge>
            </HeaderStats>
          </div>
        </HeaderLeft>
        <RefreshButton onClick={() => fetchChatHistory()}>
          Refresh
        </RefreshButton>
      </Header>

      <ChatList>
        {chatHistory.length === 0 ? (
          <EmptyState>
            <EmptyIcon>
              <MessageCircle size={32} />
            </EmptyIcon>
            <h3>No Chat History</h3>
            <p>You don't have any chat sessions yet. Start a conversation to see your history here.</p>
          </EmptyState>
        ) : (
          <AnimatePresence>
            {sortChatsByPriority(chatHistory).map((chat, index) => (
              <ChatItem
                key={chat.sessionId}
                hasDealerConnection={chat.sessionInfo.hasDealerConnection}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                onClick={() => handleChatClick(chat)}
              >
                <ChatHeader>
                  <SessionInfo>
                    <SessionIcon hasDealerConnection={chat.sessionInfo.hasDealerConnection}>
                      {getSessionIcon(chat.sessionInfo.hasDealerConnection)}
                      {chat.sessionInfo.hasDealerConnection && (
                        <ConnectionStatus connected={chat.sessionInfo.customerConnected} />
                      )}
                    </SessionIcon>
                    <SessionDetails>
                      <SessionTitle>
                        {chat.sessionInfo.hasDealerConnection ? 'Dealer Chat' : 'AI Assistant Chat'}
                      </SessionTitle>
                      <SessionSubtitle>
                        Session: {chat.sessionId.substring(0, 8)}...
                      </SessionSubtitle>
                      {chat.chatStats?.firstMessageTime && (
                        <SessionCreatedAt>
                          Started: {formatSessionCreatedAt(chat.chatStats.firstMessageTime)}
                        </SessionCreatedAt>
                      )}
                    </SessionDetails>
                  </SessionInfo>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {chat.sessionInfo.hasDealerConnection && (
                      <DealerConnectionBadge>
                        <Users size={12} />
                        Dealer Connected
                      </DealerConnectionBadge>
                    )}
                    <ChatTime>
                      <Clock size={14} />
                      {formatTime(chat.latestMessage?.timestamp)}
                    </ChatTime>
                  </div>
                </ChatHeader>

                {chat.dealer && (
                  <DealerInfo>
                    <DealerIcon>👨‍💼</DealerIcon>
                    <DealerDetails>
                      <DealerTitle>{chat.dealer.name}</DealerTitle>
                      <DealerSubtitle>
                        {chat.dealer.city} • {chat.dealer.phone}
                      </DealerSubtitle>
                    </DealerDetails>
                  </DealerInfo>
                )}

                {chat.vehicle && (
                  <VehicleInfo>
                    <VehicleIcon>🚗</VehicleIcon>
                    <VehicleDetails>
                      <VehicleTitle>
                        {chat.vehicle.year} {chat.vehicle.make} {chat.vehicle.model}
                        {chat.vehicle.trim && ` ${chat.vehicle.trim}`}
                      </VehicleTitle>
                      <VehicleSpecs>
                        <span>{chat.vehicle.mileage ? `${chat.vehicle.mileage.toLocaleString()} miles` : 'N/A'}</span>
                        <span>{chat.vehicle.condition || 'Used'}</span>
                        <span>{chat.vehicle.transmission || 'N/A'}</span>
                        <span>{formatPrice(chat.vehicle.price)}</span>
                      </VehicleSpecs>
                    </VehicleDetails>
                  </VehicleInfo>
                )}

                {chat.latestMessage && (
                  <LatestMessage>
                    <MessageContent>
                      {chat.latestMessage.content}
                    </MessageContent>
                    <MessageMeta>
                      <span>{chat.latestMessage.type === 'user' ? 'You' : chat.latestMessage.type === 'dealer' ? 'Dealer' : 'AI Assistant'}</span>
                      <span>{formatTime(chat.latestMessage.timestamp)}</span>
                    </MessageMeta>
                  </LatestMessage>
                )}

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <ChatStats>
                    <StatItem>
                      <MessageCircle size={14} />
                      {chat.chatStats.totalMessages} messages
                    </StatItem>
                    {chat.sessionInfo.roomPrice && (
                      <StatItem>
                        <DollarSign size={14} />
                        Room: {formatPrice(chat.sessionInfo.roomPrice)}
                      </StatItem>
                    )}
                    {chat.offer && (
                      <OfferInfo>
                        <DollarSign size={14} />
                        Offer: {formatPrice(chat.offer.amount)} ({chat.offer.status})
                        {chat.offer.createdAt && (
                          <span style={{ fontSize: '0.7rem', opacity: 0.8 }}>
                            • {formatTime(chat.offer.createdAt)}
                          </span>
                        )}
                      </OfferInfo>
                    )}
                  </ChatStats>
                  <ChevronRight size={16} color="rgba(255, 255, 255, 0.5)" />
                </div>
              </ChatItem>
            ))}
          </AnimatePresence>
        )}
      </ChatList>
    </Container>
  );
};

export default UserChatHistory; 